/**
 * Route Mapper Utility
 *
 * Maps routes between CLIENT and WORKER actors while preserving page context
 *
 * Example:
 * - /en/client/chat → /en/worker/chat (when switching to WORKER)
 * - /fr/worker/dashboard → /fr/client/dashboard (when switching to CLIENT)
 *
 * This provides "context-aware navigation" - user stays on the same page type
 * when switching actors, which is better UX than always redirecting to dashboard.
 */

import { ActorType } from '@hummii/shared-types';

/**
 * Route mapping configuration
 *
 * Maps CLIENT pages to their WORKER equivalents
 * If a page doesn't have an equivalent, it falls back to dashboard
 *
 * PUBLIC_PAGES: Pages without actor prefix (accessible by both roles)
 */
const ROUTE_MAPPINGS: Record<string, { client: string; worker: string }> = {
  // Dashboards
  dashboard: {
    client: 'dashboard',
    worker: 'dashboard',
  },

  // Chat pages
  chat: {
    client: 'chat',
    worker: 'chat',
  },

  // Orders/Jobs pages
  orders: {
    client: 'orders',
    worker: 'orders', // Worker sees their jobs here
  },

  // Profile pages (same for both)
  profile: {
    client: 'profile',
    worker: 'profile',
  },

  // Settings pages (same for both)
  settings: {
    client: 'settings',
    worker: 'settings',
  },

  // CLIENT-specific pages (fallback to dashboard for WORKER)
  search: {
    client: 'search', // CLIENT searches for workers
    worker: 'dashboard', // WORKER doesn't search, show dashboard
  },

  // WORKER-specific pages (fallback to dashboard for CLIENT)
  jobs: {
    client: 'dashboard', // CLIENT doesn't browse jobs
    worker: 'jobs', // WORKER browses available jobs
  },
};

/**
 * Public pages that don't require actor prefix
 * These pages are accessible by both CLIENT and WORKER
 * When switching roles on these pages, user stays on the same page
 */
const PUBLIC_PAGES = [
  '', // Home page (root)
  'profile', // Unified profile page (auto-detects role)
  'search',
  'categories',
  'categories/map',
  'login',
  'verify-email',
  'design-system',
  'test-page',
  'test-modals',
  'chat-demo',
] as const;

/**
 * Extract the page name from a pathname
 *
 * Examples:
 * - "/en/client/chat" → { pageName: "chat", isPublic: false }
 * - "/fr/worker/dashboard" → { pageName: "dashboard", isPublic: false }
 * - "/en/search" → { pageName: "search", isPublic: true }
 * - "/en" → { pageName: "", isPublic: true } (home page)
 * - "/en/categories/map" → { pageName: "categories/map", isPublic: true }
 * - "/en/profile?tab=orders" → { pageName: "profile", isPublic: true }
 *
 * @param pathname - Full pathname from router
 * @returns Object with pageName and isPublic flag, or null if not recognized
 */
function extractPageName(pathname: string): { pageName: string; isPublic: boolean } | null {
  // Remove locale prefix (e.g., /en/ or /fr/)
  const withoutLocale = pathname.replace(/^\/(en|fr)(\/|$)/, '/');

  // Check if it has actor prefix
  const hasActorPrefix = /^\/(client|worker)\//.test(withoutLocale);

  // Remove actor prefix if exists
  const withoutActor = withoutLocale.replace(/^\/(client|worker)\//, '/');

  // Get segments after removing prefixes
  const segments = withoutActor.split('/').filter(Boolean);

  // Handle home page (no segments)
  if (segments.length === 0) {
    return { pageName: '', isPublic: true };
  }

  // Reconstruct page path (for nested routes like categories/map)
  const pageName = segments.join('/');

  // Check if exact path is a public page
  if (PUBLIC_PAGES.includes(pageName as any)) {
    return { pageName, isPublic: true };
  }

  // Extract first segment for public page prefix check
  const firstSegment = segments[0];

  // Check if first segment is a public page (e.g., /profile/orders/123)
  if (PUBLIC_PAGES.includes(firstSegment as any)) {
    // Keep full path for nested public pages
    return { pageName, isPublic: true };
  }

  // Check if this page exists in our mappings (actor-specific pages)
  if (ROUTE_MAPPINGS[firstSegment]) {
    return { pageName: firstSegment, isPublic: false };
  }

  // Unknown page - treat as public if no actor prefix
  if (!hasActorPrefix) {
    return { pageName, isPublic: true };
  }

  // Default fallback for unrecognized actor-specific pages
  return null;
}

/**
 * Map current route to the equivalent route for a different actor type
 *
 * @param currentPathname - Current pathname with optional query/hash (e.g., "/en/client/chat?tab=orders#section")
 * @param targetActorType - Target actor type to map to
 * @returns Mapped pathname for the target actor type with preserved query/hash
 *
 * @example
 * ```ts
 * mapRouteForActor('/en/client/chat', ActorType.WORKER)
 * // Returns: '/en/worker/chat'
 *
 * mapRouteForActor('/fr/worker/dashboard', ActorType.CLIENT)
 * // Returns: '/fr/client/dashboard'
 *
 * mapRouteForActor('/en/search', ActorType.WORKER)
 * // Returns: '/en/search' (public page, stays the same)
 *
 * mapRouteForActor('/en/profile?tab=orders', ActorType.WORKER)
 * // Returns: '/en/profile?tab=orders' (preserves query params)
 *
 * mapRouteForActor('/en/client/search', ActorType.WORKER)
 * // Returns: '/en/worker/dashboard' (no equivalent page)
 * ```
 */
export function mapRouteForActor(
  currentPathname: string,
  targetActorType: ActorType
): string {
  // Parse URL to separate pathname, search, and hash
  let pathname = currentPathname;
  let search = '';
  let hash = '';

  // Extract hash (e.g., #section)
  const hashIndex = currentPathname.indexOf('#');
  if (hashIndex !== -1) {
    hash = currentPathname.slice(hashIndex);
    pathname = currentPathname.slice(0, hashIndex);
  }

  // Extract query string (e.g., ?tab=orders)
  const searchIndex = pathname.indexOf('?');
  if (searchIndex !== -1) {
    search = pathname.slice(searchIndex);
    pathname = pathname.slice(0, searchIndex);
  }

  // Extract locale (en or fr)
  const localeMatch = pathname.match(/^\/(en|fr)(\/|$)/);
  const locale = localeMatch?.[1] || 'en';

  // Extract page info
  const pageInfo = extractPageName(pathname);

  // If page is public, stay on the same page (just update actor context internally)
  if (pageInfo?.isPublic) {
    // For public pages, keep the same URL
    return `/${locale}/${pageInfo.pageName || ''}${search}${hash}`.replace(/\/+$/, '') || `/${locale}`;
  }

  // Determine target route for actor-specific pages
  let targetPage: string;

  if (pageInfo && ROUTE_MAPPINGS[pageInfo.pageName]) {
    // Map to equivalent page
    if (targetActorType === ActorType.CLIENT) {
      targetPage = ROUTE_MAPPINGS[pageInfo.pageName].client;
    } else if (targetActorType === ActorType.WORKER) {
      targetPage = ROUTE_MAPPINGS[pageInfo.pageName].worker;
    } else {
      // SUPPORT, STORE, etc. - fallback to dashboard
      targetPage = 'dashboard';
    }
  } else {
    // Unknown page - fallback to dashboard
    targetPage = 'dashboard';
  }

  // Construct the new path
  const actorPath =
    targetActorType === ActorType.CLIENT
      ? 'client'
      : targetActorType === ActorType.WORKER
      ? 'worker'
      : 'dashboard'; // For SUPPORT, STORE, etc.

  return `/${locale}/${actorPath}/${targetPage}${search}${hash}`;
}

/**
 * Check if a route is a public page (accessible without actor prefix)
 *
 * @param pathname - Current pathname
 * @returns true if public, false if actor-specific
 *
 * @example
 * ```ts
 * isPublicRoute('/en/search') // true
 * isPublicRoute('/en/client/dashboard') // false
 * isPublicRoute('/en') // true (home page)
 * ```
 */
export function isPublicRoute(pathname: string): boolean {
  const pageInfo = extractPageName(pathname);
  return pageInfo?.isPublic ?? true;
}

/**
 * Check if current route is accessible by the given actor type
 *
 * @param pathname - Current pathname
 * @param actorType - Actor type to check
 * @returns true if accessible, false otherwise
 *
 * @example
 * ```ts
 * isRouteAccessible('/en/client/dashboard', ActorType.CLIENT) // true
 * isRouteAccessible('/en/client/dashboard', ActorType.WORKER) // false
 * isRouteAccessible('/en/search', ActorType.CLIENT) // true (public page)
 * ```
 */
export function isRouteAccessible(
  pathname: string,
  actorType: ActorType
): boolean {
  // Public pages are accessible by everyone
  if (isPublicRoute(pathname)) {
    return true;
  }

  // Check if pathname contains the correct actor segment
  if (pathname.includes('/client/')) {
    return actorType === ActorType.CLIENT;
  }

  if (pathname.includes('/worker/')) {
    return actorType === ActorType.WORKER;
  }

  // Default to accessible if no actor segment found
  return true;
}

/**
 * Get default dashboard path for an actor type
 *
 * @param actorType - Actor type
 * @param locale - Locale (default: 'en')
 * @returns Dashboard path
 */
export function getDefaultDashboard(
  actorType: ActorType,
  locale: string = 'en'
): string {
  if (actorType === ActorType.CLIENT) {
    return `/${locale}/client/dashboard`;
  }

  if (actorType === ActorType.WORKER) {
    return `/${locale}/worker/dashboard`;
  }

  // Fallback for SUPPORT, STORE, etc.
  return `/${locale}/dashboard`;
}
